// Advisor drawings, and the figures they were drawn over.
//
// Two things make this different from the whiteboard the older report already
// has. The advisor draws across the whole page rather than inside one board, and
// what they draw has to survive being looked at again after the numbers have
// moved.
//
// The prototype solved the second problem by remembering which element each
// point sat inside and re-placing it after the layout reflowed. This does not,
// on the brief's own instruction: an anchor that still exists is not a promise
// that it still means the same thing, and a mark that silently relocates onto a
// different word is worse than one that is plainly out of date. Instead every
// drawing carries an immutable copy of the figures that were on screen when the
// ink went on, and the takeaway re-renders those figures at a fixed width and
// lays the ink back over them. Yesterday's ink is always shown over yesterday's
// numbers.
//
// Coordinates are fractions of the rendered width, for both axes. That keeps a
// stroke's shape at any window size, and lets the page be as tall as it needs to
// be without squashing the drawing.
//
// Storage rides the sketches part that already syncs to Salesforce. The older
// report's two boards live at `sketches.cashflow` and `sketches.tax` and are not
// touched; these live beside them under `sketches.illustrations`.

export const ILLUSTRATION_VERSION = 1;

// Five markers, named rather than described, because an advisor picks a colour
// by name when they talk about it afterwards. Pure blue leads because it is the
// firm's and because the prototype defaulted to it.
export const INK_COLORS = Object.freeze([
  { key: 'blue', label: 'Pure blue', hex: '#015697' },
  { key: 'red', label: 'Red', hex: '#a83e34' },
  { key: 'green', label: 'Green', hex: '#1a7a4a' },
  { key: 'purple', label: 'Purple', hex: '#6b4c9a' },
  { key: 'charcoal', label: 'Charcoal', hex: '#33414d' },
]);

export const DEFAULT_INK = INK_COLORS[0].hex;
export const STROKE_WIDTH = 3;

// Points closer together than this add nothing a reader can see and cost the
// field limit real characters.
const MIN_STEP = 0.0015;
const PRECISION = 4;

function round(n) {
  return Number(n.toFixed(PRECISION));
}

export function newIllustration({ kind, title, step = null, board = null, basis = null, now = null }) {
  const stamp = now || new Date().toISOString();
  return {
    v: ILLUSTRATION_VERSION,
    id: `ill-${stamp.replace(/[^0-9]/g, '').slice(0, 14)}-${Math.random().toString(36).slice(2, 7)}`,
    kind: kind === 'board' ? 'board' : 'moment',
    title: title || '',
    step,
    board,
    unit: 'width',
    strokes: [],
    basis,
    created: stamp,
    updated: stamp,
  };
}

export function startStroke(color) {
  return { c: color || DEFAULT_INK, w: STROKE_WIDTH, p: [] };
}

// Returns a new stroke rather than mutating, so React state updates stay honest.
export function extendStroke(stroke, x, y) {
  const px = round(x);
  const py = round(y);
  const last = stroke.p[stroke.p.length - 1];
  if (last && Math.abs(last[0] - px) < MIN_STEP && Math.abs(last[1] - py) < MIN_STEP) return stroke;
  return { ...stroke, p: [...stroke.p, [px, py]] };
}

// Every position the pointer reported since the last event, not just the last
// one. A pen samples far faster than the screen redraws, and the browser folds
// those samples into one event per frame; reading only the event draws a
// fast curve as a string of straight segments, which is the "sloppy" an
// advisor sees (Dominic Knauf, 2026-09-28). Synthetic events and older
// browsers return nothing here, so the event itself is the fallback.
export function pointerSamples(e) {
  const list = e && typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : null;
  return list && list.length ? list : [e];
}

// Whether a pointer that has just touched down may draw, given the one already
// drawing (`active` is { id, type } or null). One stroke at a time, so a second
// finger or a resting palm cannot hijack the line in progress. A pen outranks a
// touch: a palm that lands a moment before the pen tip has its half-stroke
// thrown away rather than the pen being ignored.
export function claimPointer(active, e) {
  if (!active) return 'start';
  if (e.pointerType === 'pen' && active.type !== 'pen') return 'replace';
  return 'ignore';
}

export function hasInk(illustration) {
  return Boolean(illustration && Array.isArray(illustration.strokes) && illustration.strokes.length);
}

// Readers are defensive about the shape of a saved stroke on purpose. A record
// written by an older build, or damaged in storage, must degrade to "no ink"
// rather than throw: this runs while a prospect is looking at the screen, and a
// blank page is a far worse answer than a missing mark.
function pointsOf(stroke) {
  const p = stroke && stroke.p;
  if (!Array.isArray(p)) return [];
  return p.filter(pt => Array.isArray(pt) && Number.isFinite(pt[0]) && Number.isFinite(pt[1]));
}

export function strokesOf(illustration) {
  const list = illustration && Array.isArray(illustration.strokes) ? illustration.strokes : [];
  return list.filter(s => s && typeof s === 'object');
}

export function commitStroke(illustration, stroke, now = null) {
  if (!stroke || !stroke.p.length) return illustration;
  return {
    ...illustration,
    strokes: [...illustration.strokes, stroke],
    updated: now || new Date().toISOString(),
  };
}

export function undoStroke(illustration, now = null) {
  if (!hasInk(illustration)) return illustration;
  return {
    ...illustration,
    strokes: illustration.strokes.slice(0, -1),
    updated: now || new Date().toISOString(),
  };
}

// Clear empties the ink and leaves the record, because the title and the basis
// are the advisor's work too. A record with no ink is dropped on save.
export function clearStrokes(illustration, now = null) {
  return { ...illustration, strokes: [], updated: now || new Date().toISOString() };
}

// The Illustrations section of the meeting is a set of blank whiteboards
// (Jimmy, 2026-10-03). Each is a page drawing like any other, on the
// whiteboard page, told apart by its board key: b1, b2 and so on, in the
// order they were started. A board exists once it has ink; an empty one is
// only the sheet the advisor is looking at.
export const BOARD_STEP = 'whiteboard';
export const FIRST_BOARD = 'b1';

const boardNumber = key => {
  const m = /^b(\d+)$/.exec(String(key || ''));
  return m ? Number(m[1]) : 1;
};

/** The whiteboards that carry ink, in order, as `{ key, illustration }`. */
export function listBoards(sketches) {
  const byKey = new Map();
  for (const i of listIllustrations(sketches)) {
    if (i.kind === 'moment' && i.step === BOARD_STEP && hasInk(i)) byKey.set(i.board || FIRST_BOARD, i);
  }
  return [...byKey.entries()]
    .map(([key, illustration]) => ({ key, illustration }))
    .sort((a, b) => boardNumber(a.key) - boardNumber(b.key));
}

/** The key for a new board, after every key in use. */
export function nextBoardKey(keys = []) {
  return `b${Math.max(0, ...keys.map(boardNumber)) + 1}`;
}

/** The board keys in order, with `extra` (the blank sheet on screen) among them. */
export function boardKeys(sketches, extra = null) {
  const keys = listBoards(sketches).map(b => b.key);
  if (extra && !keys.includes(extra)) keys.push(extra);
  return keys.sort((a, b) => boardNumber(a) - boardNumber(b));
}

export function listIllustrations(sketches) {
  const list = sketches && Array.isArray(sketches.illustrations) ? sketches.illustrations : [];
  return list.filter(i => i && typeof i === 'object' && i.id);
}

/**
 * Put a drawing into the sketches slice, replacing any earlier version of the
 * same one and dropping it entirely when it carries neither ink nor a title. An
 * untouched board the advisor opened and closed should not become a record.
 */
export function putIllustration(sketches, illustration) {
  const base = sketches && typeof sketches === 'object' ? sketches : {};
  const rest = listIllustrations(base).filter(i => i.id !== illustration.id);
  const worthKeeping = hasInk(illustration) || Boolean((illustration.title || '').trim());
  const next = worthKeeping ? [...rest, illustration] : rest;
  return { ...base, illustrations: next };
}

export function removeIllustration(sketches, id) {
  const base = sketches && typeof sketches === 'object' ? sketches : {};
  return { ...base, illustrations: listIllustrations(base).filter(i => i.id !== id) };
}

/**
 * The drawing that belongs to where the advisor is standing.
 *
 * A moment's drawing is found by its step and board, so returning to a moment
 * finds the ink already there. A changed basis does not silently attach to the
 * old ink: the caller decides, and the drawer keeps both.
 */
export function findForMoment(sketches, step, board, basis = null) {
  return listIllustrations(sketches).slice().reverse().find(
    i => i.kind === 'moment' && i.step === step && (i.board || null) === (board || null) && (!basis || sameBasis(i.basis, basis)),
  ) || null;
}

/**
 * The immutable copy of what was on screen.
 *
 * Deliberately the displayed figures rather than the whole questionnaire: it is
 * what the drawing has to be honest about, it is small enough to sit inside a
 * long text field beside every other drawing, and it cannot carry a private note
 * into a client artefact because none of these fields is one.
 */
export function basisFor(facts, comparison, step, board, analysis = null, meeting = null) {
  const num = f => (f && f.state === 'known' ? f.value : null);
  return {
    v: ILLUSTRATION_VERSION,
    step,
    board: board || null,
    analysis: (board === 'portfolio' || step === 'investments' || step === 'costs') ? analysis : null,
    // The advisor's choices for this meeting move figures and rows around
    // (which years section 2 reads, which callouts are left out), so a
    // drawing made over one arrangement is kept with it.
    meeting: meeting ? { period: meeting.period || null, hidden: [...(meeting.hidden || [])].sort() } : null,
    name: facts.name,
    period: facts.period,
    status: facts.status && facts.status.state === 'known' ? facts.status.raw : null,
    retirement: num(facts.retirement),
    retirementState: facts.retirement ? facts.retirement.state : null,
    spending: num(facts.spending),
    spendingState: facts.spending ? facts.spending.state : null,
    income: num(facts.income),
    incomeComplete: Boolean(facts.income && facts.income.complete),
    incomeRows: (facts.income?.rows || []).map(r => [r.label, num(r.amount), r.kind || null]),
    assets: num(facts.assets),
    liabilities: num(facts.liabilities),
    net: num(facts.net),
    assetsPartial: Boolean(facts.assetsPartial),
    pools: {
      taxDeferred: num(facts.pools?.taxDeferred),
      roth: num(facts.pools?.roth),
      taxable: num(facts.pools?.taxable),
    },
    unclassified: num(facts.unclassified),
    planningFocus: facts.planningFocus || '',
    comparison: comparison
      ? { ready: comparison.ready, gap: comparison.gap, direction: comparison.direction }
      : null,
  };
}

// Two bases are the same when every figure a drawing could have been made over
// is the same. Field order is fixed by `basisFor`, so a string compare is safe
// and is far cheaper than walking the object on every render.
export function sameBasis(a, b) {
  if (!a || !b) return false;
  // Store hydration adds bookkeeping ids inside arrays; they do not change
  // the displayed figures and must not invalidate a saved portfolio drawing.
  const visible = (key, value) => key === '_uid' ? undefined : value;
  return JSON.stringify(a, visible) === JSON.stringify(b, visible);
}

/** The points of a stroke as an SVG polyline, scaled to a rendered width. */
export function strokePoints(stroke, width) {
  return pointsOf(stroke).map(([x, y]) => `${round(x * width)},${round(y * width)}`).join(' ');
}

/**
 * How tall a drawing needs its canvas to be, in the same width-fraction unit.
 * A drawing made on a long page must not be cropped when it is replayed.
 */
export function inkExtent(illustration) {
  let maxY = 0;
  for (const s of strokesOf(illustration)) {
    for (const [, y] of pointsOf(s)) if (y > maxY) maxY = y;
  }
  return maxY;
}

/** What the drawings cost against the field they share, so the UI can say so. */
export function illustrationsBytes(sketches) {
  try {
    return JSON.stringify(listIllustrations(sketches)).length;
  } catch {
    return 0;
  }
}
